import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { findOverlappingSlots, formatOverlapError, TimeSlotForOverlapCheck } from "@/lib/timeSlotOverlapUtils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { validateSlotTimes } from "@/lib/serviceOrder";

interface TimeSlotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    id?: string;
    startTime: string;
    endTime: string;
    availableDays: number[];
    isActive: boolean;
    durationOverrideMinutes?: number | null;
  }) => Promise<void>;
  editingSlot?: {
    id: string;
    startTime: string;
    endTime: string;
    availableDays: number[];
    isActive: boolean;
    durationOverrideMinutes?: number | null;
  } | null;
  existingSlots?: TimeSlotForOverlapCheck[];
  bookingMode?: string;
  defaultDurationMinutes?: number;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Lundi" },
  { value: 2, label: "Mardi" },
  { value: 3, label: "Mercredi" },
  { value: 4, label: "Jeudi" },
  { value: 5, label: "Vendredi" },
  { value: 6, label: "Samedi" },
  { value: 0, label: "Dimanche" },
];

export function TimeSlotDialog({ open, onOpenChange, onSubmit, editingSlot, existingSlots = [], bookingMode, defaultDurationMinutes }: TimeSlotDialogProps) {
  const [startTime, setStartTime] = useState("12:00");
  const [endTime, setEndTime] = useState("14:00");
  const [availableDays, setAvailableDays] = useState<number[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [durationOverride, setDurationOverride] = useState<string>("");

  // Pré-remplir les champs si on édite
  useEffect(() => {
    if (editingSlot) {
      setStartTime(editingSlot.startTime);
      setEndTime(editingSlot.endTime);
      setAvailableDays(editingSlot.availableDays);
      setIsActive(editingSlot.isActive);
      setDurationOverride(editingSlot.durationOverrideMinutes ? String(editingSlot.durationOverrideMinutes) : "");
    } else {
      // Reset si on crée un nouveau créneau
      setStartTime("12:00");
      setEndTime("14:00");
      setAvailableDays([]);
      setIsActive(true);
      setDurationOverride("");
    }
  }, [editingSlot, open]);

  // Calcul du chevauchement en temps réel
  const overlapWarning = useMemo(() => {
    if (!startTime || !endTime) return null;
    
    const overlapping = findOverlappingSlots(
      {
        id: editingSlot?.id,
        start_time: startTime,
        end_time: endTime,
        available_days: availableDays
      },
      existingSlots
    );
    
    if (overlapping.length > 0) {
      return formatOverlapError(overlapping);
    }
    return null;
  }, [startTime, endTime, availableDays, existingSlots, editingSlot?.id]);

  const toggleDay = (day: number) => {
    setAvailableDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const slotTimeError = validateSlotTimes(startTime, endTime);

  const handleSubmit = async () => {
    if (!startTime || !endTime || slotTimeError) return;
    // Le chevauchement est un avertissement, pas un blocage

    setIsSubmitting(true);
    try {
      const parsedDuration = durationOverride ? parseInt(durationOverride) : null;
      await onSubmit({
        id: editingSlot?.id,
        startTime,
        endTime,
        availableDays,
        isActive,
        durationOverrideMinutes: parsedDuration && parsedDuration > 0 ? parsedDuration : null,
      });
      // Reset form si création
      if (!editingSlot) {
        setStartTime("12:00");
        setEndTime("14:00");
        setAvailableDays([]);
        setIsActive(true);
        setDurationOverride("");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const setQuickPreset = (preset: 'all' | 'weekday' | 'weekend') => {
    if (preset === 'all') {
      setAvailableDays([0, 1, 2, 3, 4, 5, 6]); // Tous les jours
    } else if (preset === 'weekday') {
      setAvailableDays([1, 2, 3, 4, 5]); // Lun-Ven
    } else if (preset === 'weekend') {
      setAvailableDays([0, 6]); // Sam-Dim
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editingSlot ? "Modifier le créneau" : "Nouveau créneau horaire"}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] overflow-y-auto">
        <div className="space-y-4 py-4 pr-4">
          {/* Presets rapides */}
          <div className="space-y-2">
            <Label>Raccourcis</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setQuickPreset('all')}
                className={availableDays.length === 7 ? "border-primary" : ""}
              >
                Tous les jours
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setQuickPreset('weekday')}
                className={availableDays.length === 5 && availableDays.includes(1) && availableDays.includes(5) ? "border-primary" : ""}
              >
                Semaine (L-V)
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setQuickPreset('weekend')}
                className={availableDays.length === 2 && availableDays.includes(0) && availableDays.includes(6) ? "border-primary" : ""}
              >
                Weekend
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="startTime">Heure de début</Label>
            <Input
              id="startTime"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="endTime">Heure de fin</Label>
            <Input
              id="endTime"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
            {slotTimeError ? (
              <p className="text-xs text-destructive">{slotTimeError}</p>
            ) : endTime && startTime && endTime.substring(0, 5) < startTime.substring(0, 5) ? (
              <p className="text-xs text-muted-foreground">Ce service se termine le lendemain à {endTime.substring(0, 5)}. Les réservations restent rattachées au jour de début.</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>Jours de la semaine</Label>
            <div className="text-xs text-muted-foreground mb-2">
              {availableDays.length === 0 
                ? "Aucun jour sélectionné = Tous les jours" 
                : `${availableDays.length} jour(s) sélectionné(s)`}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <Button
                  key={day.value}
                  type="button"
                  variant={availableDays.includes(day.value) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleDay(day.value)}
                  className="justify-start"
                >
                  {availableDays.includes(day.value) && "✓ "}
                  {day.label}
                </Button>
              ))}
            </div>
          </div>
          {/* Durée personnalisée (mode TIME uniquement) */}
          {bookingMode === 'time' && (
            <div className="space-y-2">
              <Label htmlFor="durationOverride">Durée personnalisée (minutes)</Label>
              <Input
                id="durationOverride"
                type="number"
                min="15"
                max="480"
                step="15"
                placeholder={defaultDurationMinutes ? `${defaultDurationMinutes} (par défaut)` : "Durée globale par défaut"}
                value={durationOverride}
                onChange={(e) => setDurationOverride(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Laissez vide pour utiliser la durée globale de l'établissement. Utile pour un brunch plus court par exemple.
              </p>
            </div>
          )}

          {overlapWarning && (
            <Alert className="border-orange-500/50 text-orange-700 dark:text-orange-400 [&>svg]:text-orange-500">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p>{overlapWarning}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Cela peut être intentionnel si les services ne génèrent pas de réservations aux mêmes heures.
                </p>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">Actif</Label>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>
        </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !!slotTimeError}>
            {isSubmitting ? (editingSlot ? "Modification..." : "Création...") : (editingSlot ? "Modifier" : "Créer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
